import { useState } from "react"
import { AdminLayout } from "@/components/Admin/AdminLayout"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Link, router } from "@inertiajs/react"
import { Search, Download, ChevronLeft, ChevronRight, Clock } from "lucide-react"

interface OrderItem {
  id: number
  user_email: string
  user_name: string
  total_cents: number
  status: string
  asaas_checkout_id?: string | null
  created_at: string
  paid_at?: string | null
  can_expire?: boolean
}

interface Filters {
  status?: string | null
  date_from?: string | null
  date_to?: string | null
  search?: string | null
  user_name?: string | null
  user_email?: string | null
}

interface Pagination {
  page: number
  per_page: number
  total_count: number
  total_pages: number
}

interface Props {
  orders: OrderItem[]
  filters: Filters
  pagination: Pagination
  stats: {
    total: number
    paid: number
    pending: number
    expired: number
    failed: number
    total_revenue_cents: number
  }
}

const formatCents = (cents: number) => (cents / 100).toFixed(2).replace(".", ",")

const formatDateTime = (value?: string | null) => {
  if (!value) return "-"
  const date = new Date(value)
  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(date)
}

const getStatusBadgeClass = (status: string) => {
  switch (status) {
    case "paid":
      return "bg-green-100 text-green-800"
    case "pending":
      return "bg-yellow-100 text-yellow-800"
    case "failed":
      return "bg-red-100 text-red-800"
    case "expired":
      return "bg-gray-200 text-gray-800"
    default:
      return "bg-gray-100 text-gray-800"
  }
}

const getStatusLabel = (status: string) => {
  switch (status) {
    case "paid":
      return "Pago"
    case "pending":
      return "Pendente"
    case "failed":
      return "Falhou"
    case "expired":
      return "Expirado"
    default:
      return status
  }
}

export default function OrdersIndex({ orders, filters, pagination, stats }: Props) {
  const [searchValue, setSearchValue] = useState(filters.search || "")
  const [expiringId, setExpiringId] = useState<number | null>(null)

  const applyFilters = (newFilters: Partial<Filters>) => {
    const mergedFilters = {
      status: filters.status,
      date_from: filters.date_from,
      date_to: filters.date_to,
      search: filters.search,
      user_name: filters.user_name,
      user_email: filters.user_email,
      ...newFilters,
      page: 1, // Reset to first page on filter change
    }

    // Remove empty values
    const cleanFilters = Object.fromEntries(
      Object.entries(mergedFilters).filter(([_, v]) => v !== null && v !== undefined && v !== "")
    )

    router.get("/admin/orders", cleanFilters, { preserveScroll: true })
  }

  const goToPage = (page: number) => {
    if (page < 1 || page > pagination.total_pages) return
    router.get(
      "/admin/orders",
      {
        ...filters,
        page,
      },
      { preserveScroll: true }
    )
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    applyFilters({ search: searchValue || null })
  }

  const exportCsv = () => {
    const params = new URLSearchParams()
    if (filters.status) params.append("status", filters.status)
    if (filters.date_from) params.append("date_from", filters.date_from)
    if (filters.date_to) params.append("date_to", filters.date_to)
    if (filters.search) params.append("search", filters.search)

    window.location.href = `/admin/orders.csv?${params.toString()}`
  }

  const expireOrder = (orderId: number) => {
    const reason = window.prompt("Motivo para expirar este pedido? (opcional)")?.trim()
    setExpiringId(orderId)
    router.post(
      `/admin/orders/${orderId}/expire`,
      reason ? { reason } : {},
      {
        preserveScroll: true,
        onFinish: () => setExpiringId(null),
      }
    )
  }

  return (
    <AdminLayout title="Pedidos">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Card className="p-4">
          <p className="text-sm text-muted-foreground">Total</p>
          <p className="text-2xl font-bold">{stats.total}</p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-muted-foreground">Pagos</p>
          <p className="text-2xl font-bold text-green-600">{stats.paid}</p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-muted-foreground">Pendentes</p>
          <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-muted-foreground">Expirados</p>
          <p className="text-2xl font-bold text-gray-700">{stats.expired}</p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-muted-foreground">Falhou</p>
          <p className="text-2xl font-bold text-red-600">{stats.failed}</p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-muted-foreground">Receita Total</p>
          <p className="text-2xl font-bold">R$ {formatCents(stats.total_revenue_cents)}</p>
        </Card>
      </div>

      <Card className="p-4 md:p-6">
        {/* Filters Row */}
        <div className="flex flex-col gap-4 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <h2 className="text-lg font-semibold">Lista de pedidos</h2>
            <Button variant="outline" size="sm" onClick={exportCsv}>
              <Download className="h-4 w-4 mr-2" />
              Exportar CSV
            </Button>
          </div>

          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <form onSubmit={handleSearch} className="flex gap-2 flex-1 max-w-md">
              <Input
                type="text"
                placeholder="Buscar por nome ou e-mail..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
              />
              <Button type="submit" variant="outline" size="icon">
                <Search className="h-4 w-4" />
              </Button>
            </form>

            {/* Status Filter */}
            <select
              value={filters.status || ""}
              onChange={(event) => applyFilters({ status: event.target.value || null })}
              className="border rounded-md px-3 py-2 text-sm bg-white"
            >
              <option value="">Todos os status</option>
              <option value="paid">Pago</option>
              <option value="pending">Pendente</option>
              <option value="failed">Falhou</option>
              <option value="expired">Expirado</option>
            </select>

            {/* Date From */}
            <div className="flex items-center gap-2">
              <label className="text-sm text-muted-foreground whitespace-nowrap">De:</label>
              <Input
                type="date"
                value={filters.date_from || ""}
                onChange={(e) => applyFilters({ date_from: e.target.value || null })}
                className="w-auto"
              />
            </div>

            {/* Date To */}
            <div className="flex items-center gap-2">
              <label className="text-sm text-muted-foreground whitespace-nowrap">Até:</label>
              <Input
                type="date"
                value={filters.date_to || ""}
                onChange={(e) => applyFilters({ date_to: e.target.value || null })}
                className="w-auto"
              />
            </div>
          </div>
          <div className="flex flex-col md:flex-row gap-4">
            <Input
              type="text"
              placeholder="Filtrar por usuário..."
              value={filters.user_name || ""}
              onChange={(e) => applyFilters({ user_name: e.target.value || null })}
              className="max-w-sm"
            />
            <Input
              type="text"
              placeholder="Filtrar por e-mail..."
              value={filters.user_email || ""}
              onChange={(e) => applyFilters({ user_email: e.target.value || null })}
              className="max-w-sm"
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground border-b">
                <th className="py-2 pr-4">ID</th>
                <th className="py-2 pr-4">Usuário</th>
                <th className="py-2 pr-4">Email</th>
                <th className="py-2 pr-4">Total</th>
                <th className="py-2 pr-4">Status</th>
                <th className="py-2 pr-4">Criado</th>
                <th className="py-2 pr-4">Pago em</th>
                <th className="py-2">Ações</th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-8 text-center text-muted-foreground">
                    Nenhum pedido encontrado
                  </td>
                </tr>
              ) : (
                orders.map((order) => (
                  <tr key={order.id} className="border-b last:border-0">
                    <td className="py-2 pr-4">#{order.id}</td>
                    <td className="py-2 pr-4">{order.user_name || "-"}</td>
                    <td className="py-2 pr-4">{order.user_email || "-"}</td>
                    <td className="py-2 pr-4">R$ {formatCents(order.total_cents)}</td>
                    <td className="py-2 pr-4">
                      <div className="flex items-center gap-2">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(
                            order.status
                          )}`}
                        >
                          {getStatusLabel(order.status)}
                        </span>
                        {order.status === "pending" && order.can_expire && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            title="Marcar como expirado"
                            onClick={() => expireOrder(order.id)}
                            disabled={expiringId === order.id}
                          >
                            <Clock className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                    <td className="py-2 pr-4">{formatDateTime(order.created_at)}</td>
                    <td className="py-2 pr-4">{formatDateTime(order.paid_at)}</td>
                    <td className="py-2">
                      <Link href={`/admin/orders/${order.id}`} className="text-primary hover:underline">
                        Ver detalhes
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="flex items-center justify-between mt-6 pt-4 border-t">
            <p className="text-sm text-muted-foreground">
              Mostrando {(pagination.page - 1) * pagination.per_page + 1} a{" "}
              {Math.min(pagination.page * pagination.per_page, pagination.total_count)} de{" "}
              {pagination.total_count} pedidos
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => goToPage(pagination.page - 1)}
                disabled={pagination.page === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm">
                Página {pagination.page} de {pagination.total_pages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => goToPage(pagination.page + 1)}
                disabled={pagination.page === pagination.total_pages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </AdminLayout>
  )
}
